import { useQuery } from '@tanstack/react-query';
import { parseCalendarDate, salesDateRange } from '@klothiq/domain';
import { useAuth } from '@/contexts/AuthContext';
import { salesRpc } from '@/lib/salesOverview';
export type Timeframe = '7d' | '30d' | '6m' | '1y';
export interface ChartDataPoint {
  key: string;
  label: string;
  fullLabel: string;
  sales: number;
  profit?: number;
  ordersCount: number;
  collected: number;
  balanceDue: number;
}
export interface OverviewAnalyticsResponse {
  from: string;
  to: string;
  timezone: string;
  as_of: string;
  buckets: Array<{
    key: string;
    sales: number;
    profit?: number;
    orders: number;
    collected: number;
    balance_due: number;
  }>;
  receipt_buckets: Array<{ bucket: string; receipts: number; refunds: number }>;
  totals: {
    sales: number;
    orders: number;
    profit?: number;
    collected: number;
    balance_due: number;
    return_review_count: number;
    receipts: number;
    refunds: number;
  };
}
export function useSalesAnalytics(
  timeframe: Timeframe | 'month' | { from: string; to: string } = '6m',
) {
  const { organization, profile, access, can } = useAuth();
  const range =
    typeof timeframe === 'object' ? timeframe : salesDateRange(timeframe, organization?.timezone);
  return useQuery({
    queryKey: ['sales_analytics', organization?.id, profile?.id, access?.access_version, range],
    enabled: !!organization && (can('dashboard.view') || can('report_sales_summary.view')),
    staleTime: 15000,
    refetchInterval: 60000,
    queryFn: async () => {
      const result = await salesRpc<OverviewAnalyticsResponse>('get_sales_overview_analytics', {
        p_filters: range,
      });
      const end = parseCalendarDate(range.to)!,
        start = parseCalendarDate(range.from)!;
      const daily =
        (Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()) -
          Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())) /
          86400000 <=
        45;
      let chartData: ChartDataPoint[] = [];
      const day = parseCalendarDate(range.from)!;
      if (!daily) day.setDate(1);
      const buckets = new Map(result.buckets.map((row) => [row.key, row]));
      const receipts = new Map(result.receipt_buckets.map((row) => [row.bucket, row]));
      let receiptSeries: { v: number }[] = [];
      while (true) {
        const key = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
        if (key > range.to) break;
        const row = buckets.get(key);
        chartData.push({
          key,
          label: day.toLocaleDateString(
            'en-IN',
            daily ? { day: 'numeric', month: 'short' } : { month: 'short', year: '2-digit' },
          ),
          fullLabel: day.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          }),
          sales: Number(row?.sales ?? 0),
          profit: can('financials.view') ? Number(row?.profit ?? 0) : undefined,
          ordersCount: Number(row?.orders ?? 0),
          collected: Number(row?.collected ?? 0),
          balanceDue: Number(row?.balance_due ?? 0),
        });
        receiptSeries.push({ v: Number(receipts.get(key)?.receipts ?? 0) });
        if (daily) day.setDate(day.getDate() + 1);
        else day.setMonth(day.getMonth() + 1);
      }
      if (!daily && chartData.length > 24) {
        const years = new Map<string, { point: ChartDataPoint; receipts: number }>();
        chartData.forEach((point, index) => {
          const year = point.key.slice(0, 4);
          let entry = years.get(year);
          if (!entry) {
            entry = {
              point: {
                ...point,
                key: `${year}-01-01`,
                label: year,
                fullLabel: year,
                sales: 0,
                profit: point.profit === undefined ? undefined : 0,
                ordersCount: 0,
                collected: 0,
                balanceDue: 0,
              },
              receipts: 0,
            };
            years.set(year, entry);
          }
          entry.point.sales += point.sales;
          entry.point.ordersCount += point.ordersCount;
          entry.point.collected += point.collected;
          entry.point.balanceDue += point.balanceDue;
          if (entry.point.profit !== undefined) entry.point.profit += point.profit || 0;
          entry.receipts += receiptSeries[index]?.v || 0;
        });
        chartData = [...years.values()].map((x) => x.point);
        receiptSeries = [...years.values()].map((x) => ({ v: x.receipts }));
      }
      const totalSales = Number(result.totals.sales);
      const totalProfit = result.totals.profit == null ? undefined : Number(result.totals.profit);
      return {
        chartData,
        totalSales,
        totalProfit,
        grossMarginPercent:
          totalProfit === undefined
            ? undefined
            : totalSales > 0
              ? (totalProfit / totalSales) * 100
              : 0,
        totalOrders: Number(result.totals.orders),
        avgPeriodSales: chartData.length ? totalSales / chartData.length : 0,
        sparklineSales: chartData.map((p) => ({ v: p.sales })),
        sparklineProfit:
          totalProfit === undefined ? [] : chartData.map((p) => ({ v: p.profit ?? 0 })),
        sparklineReceipts: receiptSeries,
        returnReviewCount: Number(result.totals.return_review_count),
        asOf: result.as_of,
        range,
      };
    },
  });
}
